/**
 * Utility functions for extracting error messages from API responses.
 * Handles various error formats including ValidationProblemDetails.
 */

/**
 * Interface for ValidationProblemDetails response from ASP.NET Core
 */
interface ValidationProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

/**
 * Checks if the error data matches ValidationProblemDetails format
 */
function isValidationProblemDetails(data: unknown): data is ValidationProblemDetails {
  return typeof data === 'object' && data !== null && ('errors' in data || 'title' in data);
}

/**
 * Extracts a human-readable error message from an API error response.
 *
 * Handles multiple error formats:
 * 1. ValidationProblemDetails with `errors` object (from FluentValidation/DomainValidationException)
 * 2. Plain text error message (string body)
 * 3. Object with `message` property
 * 4. Object with `title` property
 *
 * @param error - The error object from RTK Query (FetchBaseQueryError or SerializedError)
 * @param fallbackMessage - Default message if no specific error can be extracted
 * @returns Human-readable error message
 *
 * @example
 * ```tsx
 * try {
 *   await someApiCall().unwrap();
 * } catch (err) {
 *   toast.error(extractApiErrorMessage(err, 'Nie udało się zapisać danych'));
 * }
 * ```
 */
export function extractApiErrorMessage(
  error: unknown,
  fallbackMessage: string = 'Wystąpił błąd'
): string {
  // Handle null/undefined
  if (!error) {
    return fallbackMessage;
  }

  // Handle SerializedError (has message property directly)
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as any).message === 'string'
  ) {
    const message = (error as any).message;
    if (message && message !== 'Rejected') {
      return message;
    }
  }

  // Handle FetchBaseQueryError (has data property)
  if (typeof error === 'object' && 'data' in error) {
    const data = (error as any).data;

    // Case 1: Plain string response
    if (typeof data === 'string' && data.trim().length > 0) {
      return data;
    }

    // Case 2: ValidationProblemDetails format
    if (isValidationProblemDetails(data)) {
      // Try to extract errors
      if (data.errors && Object.keys(data.errors).length > 0) {
        const allErrors = Object.values(data.errors).flat();
        if (allErrors.length > 0) {
          return allErrors.join(' ');
        }
      }
      // Prefer detail over generic title (e.g. "Bad Request")
      if (data.detail && data.detail.trim().length > 0) {
        return data.detail;
      }

      // Fall back to title
      if (data.title) {
        return data.title;
      }
    }

    // Case 3: Object with message property
    if (typeof data === 'object' && data !== null) {
      if ('message' in data && typeof data.message === 'string') {
        return data.message;
      }
      if ('detail' in data && typeof data.detail === 'string') {
        return data.detail;
      }
    }
  }

  // Handle FetchBaseQueryError.error for app-thrown PARSING_ERROR messages.
  // Keep PARSING_ERROR fallback below for technical parser messages (e.g. SyntaxError).
  if (typeof error === 'object' && 'error' in error && typeof (error as any).error === 'string') {
    const status = 'status' in error ? (error as any).status : undefined;
    const rawError = (error as any).error.trim();
    if (status === 'PARSING_ERROR') {
      if (rawError.startsWith('Error: ')) {
        return rawError.slice('Error: '.length);
      }

      const contractErrorMatch = rawError.match(/^[A-Za-z][A-Za-z0-9_.]*ContractError:\s*(.+)$/);
      if (contractErrorMatch && contractErrorMatch[1].trim().length > 0) {
        return contractErrorMatch[1].trim();
      }
    }
  }

  // Handle status property for network errors
  if (typeof error === 'object' && 'status' in error) {
    const status = (error as any).status;
    if (status === 'FETCH_ERROR') {
      return 'Błąd połączenia z serwerem';
    }
    if (status === 'PARSING_ERROR') {
      return 'Błąd przetwarzania odpowiedzi serwera';
    }
    if (typeof status === 'number') {
      switch (status) {
        case 400:
          return 'Nieprawidłowe dane wejściowe';
        case 401:
          return 'Brak autoryzacji';
        case 403:
          return 'Brak uprawnień';
        case 404:
          return 'Nie znaleziono zasobu';
        case 409:
          return 'Konflikt danych';
        case 422:
          return 'Błąd walidacji danych';
        case 429:
          return 'Zbyt wiele żądań - spróbuj ponownie później';
        case 500:
          return 'Błąd serwera';
        case 502:
        case 503:
        case 504:
          return 'Serwer tymczasowo niedostępny';
        default:
          return fallbackMessage;
      }
    }
  }

  return fallbackMessage;
}

/**
 * Extracts structured validation errors from API response.
 * Returns a map of field names to error messages.
 *
 * @param error - The error object from RTK Query
 * @returns Record of field names to error message arrays, or null if not a validation error
 */
export function extractValidationErrors(error: unknown): Record<string, string[]> | null {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return null;
  }

  const data = (error as any).data;

  if (isValidationProblemDetails(data) && data.errors) {
    return data.errors;
  }

  return null;
}

/**
 * Checks if the error is a validation error (HTTP 400)
 */
export function isValidationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('status' in error && (error as any).status === 400) {
    return true;
  }

  return false;
}

/**
 * Checks if the error is a not found error (HTTP 404)
 */
export function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('status' in error && (error as any).status === 404) {
    return true;
  }

  return false;
}
